import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { ApiError, api, type CurrentUser } from '@/lib/api';
import { auth, type AuthSession } from '@/lib/auth';
import { queryKeys } from '@/lib/query-keys';

// Supabase's PASSWORD_RECOVERY event fires exactly once, on the page
// load that parses the reset-link URL -- a reload or tab restore after
// that point sees a clean URL and only INITIAL_SESSION, which carries
// the same (real, valid) session with nothing marking it as a
// recovery flow. Without persisting the flag somewhere that survives
// past the one-shot event, a reload mid-flow silently drops the user
// into the app having never set a new password. sessionStorage is
// scoped to the tab and the browser session, which matches how long
// the recovery flow itself should stay "armed" -- web only, since
// native's redirect-based recovery flow isn't built yet (same gap as
// OAuth, see issue #11).
const PASSWORD_RECOVERY_KEY = 'nearkin.password-recovery';

function readPersistedRecovery(): boolean {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(PASSWORD_RECOVERY_KEY) === '1';
}

function persistRecovery(value: boolean) {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  if (value) window.sessionStorage.setItem(PASSWORD_RECOVERY_KEY, '1');
  else window.sessionStorage.removeItem(PASSWORD_RECOVERY_KEY);
}

// The nav gate this hook drives. Session presence alone is not enough:
// a Supabase Auth session can exist while the Nearkin `users` row
// doesn't yet, and that mid-signup moment must not land in the same
// place as a completed account.
//
// `needs-signup` is the state MeHandler's own doc comment calls out
// directly: 404 means "the token is valid but signup (SignupHandler)
// hasn't completed yet" -- a Supabase Auth session exists (OAuth or
// email/password succeeded) but the Nearkin `users` row doesn't, because
// SignupHandler (POST /me, age-attestation) hasn't run yet. This is
// reachable on every real signup: onAuthStateChange fires the moment
// Supabase Auth succeeds, well before the app has had a chance to POST
// the age attestation. It is distinct from both `signed-out` (no
// session at all) and `error` (something is actually broken) -- and
// must be, since folding it into either one is wrong: as `signed-out`,
// a user who just authenticated would be told they aren't; as `error`,
// a completely normal step in signup would show a "couldn't load your
// account, retry" screen with nothing to retry it into.
//
// `signed-up` deliberately carries NO Tier 0 distinction (issue #70,
// founder decision 2026-09-05): Tier 0 is an opt-in badge, never a
// gate -- the earlier split (pending-verification walled off from
// verified) made every new user hand a camera selfie to a stub checker
// before seeing one thread, the churn-killer the design doc's own
// user-lens review called out. The tier0Status travels on
// CurrentUser for the Profile surface's badge display instead.
export type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'needs-signup' }
  | { status: 'password-recovery' }
  | { status: 'error'; retry: () => void }
  | { status: 'signed-up'; user: CurrentUser };

// Pure: given the session and the /me query's current result, what state
// should the gate be in? Kept separate from the hook below so this is
// unit-testable without rendering a React tree or mocking react-query
// internals -- it's the part with actual branching logic; the hook is
// just wiring it to two I/O sources.
//
// password-recovery is checked first and short-circuits everything else
// -- a reset-link click establishes a real, valid session (same as a
// normal sign-in), so without this check a recovery visit would fall
// straight into the usual signed-out/needs-signup/signed-up branching
// below and skip "set a new password" entirely.
export function deriveAuthState(
  isPasswordRecovery: boolean,
  session: AuthSession | null | undefined,
  me: Pick<UseQueryResult<CurrentUser, Error>, 'isError' | 'error' | 'data'>,
  retry: () => void,
): AuthState {
  if (isPasswordRecovery) return { status: 'password-recovery' };
  if (session === undefined) return { status: 'loading' };
  if (session === null) return { status: 'signed-out' };

  if (me.isError) {
    if (!(me.error instanceof ApiError)) return { status: 'error', retry };
    if (me.error.status === 401) return { status: 'signed-out' };
    if (me.error.status === 404) return { status: 'needs-signup' };

    return { status: 'error', retry };
  }

  if (!me.data) return { status: 'loading' };

  // Tier 0 does not branch the gate (issue #70): passed or not, a
  // completed signup is a full member of the app. The status rides on
  // CurrentUser for display purposes (Profile's badge) only.
  return { status: 'signed-up', user: me.data };
}

// react-query's default (retry 3x with backoff) treats every failure
// as transient -- but a 401/404 from /me isn't transient, it's the
// expected response on the two most common paths (signed out,
// mid-signup). Retrying those added ~7s of spinner (1s+2s+4s backoff)
// to every single signup before reaching the age gate. Only retry
// failures that might actually resolve on their own (network blips, 5xx).
export function shouldRetryMe(failureCount: number, error: Error): boolean {
  return !(error instanceof ApiError && error.status < 500) && failureCount < 3;
}

export function useAuthState(): { state: AuthState; clearPasswordRecovery: () => void } {
  const [session, setSession] = useState<AuthSession | null | undefined>(undefined);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(readPersistedRecovery);

  useEffect(() => {
    // One subscription for both concerns -- onAuthStateChange alone
    // covers the initial read (it fires INITIAL_SESSION on subscribe,
    // so a separate supabase.auth.getSession() call here issued a
    // duplicate /me fetch on every cold start), and a second,
    // independent subscription elsewhere for PASSWORD_RECOVERY would
    // just be watching the same event stream twice.
    return auth.onAuthChange((event, next) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
        persistRecovery(true);
      }
      setSession(next);
    });
  }, []);

  const me = useQuery({
    // Keyed on user id, not on the session object -- a token refresh
    // (fires roughly hourly) produces a new session for the SAME user,
    // and must not invalidate this query. An earlier version reset to
    // a loading state on every onAuthStateChange event regardless of
    // cause, which unmounted the entire nav tree on every background
    // token refresh.
    queryKey: queryKeys.meSession(session?.userId),
    queryFn: api.me,
    enabled: !!session && !isPasswordRecovery,
    retry: shouldRetryMe,
  });

  return {
    state: deriveAuthState(isPasswordRecovery, session, me, () => me.refetch()),
    clearPasswordRecovery: () => {
      setIsPasswordRecovery(false);
      persistRecovery(false);
    },
  };
}
